// === Admin: Chemical Distributor Assignment ===
// Split-pane UI: select a chemical on the left, tick distributors on the right.
// Ported from Click2Gro PHP (ChemicalDistributorAssignmentController)

import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as BackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { CatalogueCountrySwitch, useCatalogueCountry } from '../components/CatalogueCountrySwitch';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChemicalItem  { readonly id: number; readonly product: string | null; }
interface DistributorItem {
  readonly id: number;
  readonly company: string | null;
  readonly street: string | null;
  readonly postcode: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

const BASE = '/api/v1/admin/chemical-distributor-assignment';

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function ChemicalDistributorAssignmentPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token ?? '';

  // ---- Catalogue country (both lists follow it) ----
  const [country, setCountry] = useCatalogueCountry();

  // ---- Chemicals (left panel) ----
  const [chemicals, setChemicals]     = useState<readonly ChemicalItem[]>([]);
  const [chemSearch, setChemSearch]   = useState('');
  const [chemsLoading, setChemsLoading] = useState(false);
  const [chemsError, setChemsError]   = useState<string | null>(null);

  // ---- Distributors (right panel checkboxes) ----
  const [distributors, setDistributors] = useState<readonly DistributorItem[]>([]);
  const [distSearch, setDistSearch]     = useState('');
  const [distsLoading, setDistsLoading] = useState(false);
  const [distsError, setDistsError]     = useState<string | null>(null);

  // ---- Selection ----
  const [selectedChemId, setSelectedChemId]   = useState<number | null>(null);
  const [assignedIds, setAssignedIds]         = useState<ReadonlySet<number>>(new Set());
  const [originalIds, setOriginalIds]         = useState<ReadonlySet<number>>(new Set());
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);

  // ---- Saving ----
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ---- Snackbar ----
  const [snack, setSnack] = useState<{
    open: boolean; message: string; severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  const isDirty = useMemo(() => {
    if (assignedIds.size !== originalIds.size) return true;
    for (const id of assignedIds) { if (!originalIds.has(id)) return true; }
    return false;
  }, [assignedIds, originalIds]);

  // ---------------------------------------------------------------------------
  // Load chemicals + distributors on mount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!token) return;
    void (async () => {
      setChemsLoading(true);
      setChemsError(null);
      try {
        const res = await fetch(`${getApiUrl()}${BASE}/chemicals?country=${country}`, { headers: authHeaders(token) });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as { data: ChemicalItem[] };
        setChemicals(json.data);
      } catch (e) {
        setChemsError(e instanceof Error ? e.message : 'Failed to load chemicals');
      } finally {
        setChemsLoading(false);
      }
    })();
  }, [token, country]);

  useEffect(() => {
    if (!token) return;
    void (async () => {
      setDistsLoading(true);
      setDistsError(null);
      try {
        const res = await fetch(`${getApiUrl()}${BASE}/distributors?country=${country}`, { headers: authHeaders(token) });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as { data: DistributorItem[] };
        setDistributors(json.data);
      } catch (e) {
        setDistsError(e instanceof Error ? e.message : 'Failed to load distributors');
      } finally {
        setDistsLoading(false);
      }
    })();
  }, [token, country]);

  // Switching country drops the selected chemical: it belongs to the other catalogue.
  const handleCountryChange = (c: string) => {
    if (c === country) return;
    setCountry(c);
    setSelectedChemId(null);
    setAssignedIds(new Set());
    setOriginalIds(new Set());
    setChemSearch('');
    setDistSearch('');
    setSaveError(null);
  };

  // ---------------------------------------------------------------------------
  // Load assignments when a chemical is selected
  // ---------------------------------------------------------------------------
  const loadAssignments = useCallback(async (chemId: number) => {
    if (!token) return;
    setAssignmentsLoading(true);
    setSaveError(null);
    try {
      const res = await fetch(`${getApiUrl()}${BASE}/${chemId}`, { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { assignedDistributorIds: number[] };
      const ids = new Set(json.assignedDistributorIds);
      setAssignedIds(ids);
      setOriginalIds(ids);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to load assignments', 'error');
    } finally {
      setAssignmentsLoading(false);
    }
  }, [token]);

  const handleSelectChemical = (chemId: number) => {
    if (chemId === selectedChemId) return;
    setSelectedChemId(chemId);
    setAssignedIds(new Set());
    setOriginalIds(new Set());
    setDistSearch('');
    setSaveError(null);
    void loadAssignments(chemId);
  };

  // ---------------------------------------------------------------------------
  // Toggle a distributor checkbox
  // ---------------------------------------------------------------------------
  const handleToggle = (distId: number) => {
    setAssignedIds((prev) => {
      const next = new Set(prev);
      if (next.has(distId)) { next.delete(distId); } else { next.add(distId); }
      return next;
    });
  };

  const handleSelectAll = () => {
    setAssignedIds(new Set(distributors.map((d) => d.id)));
  };

  const handleDeselectAll = () => {
    setAssignedIds(new Set());
  };

  // ---------------------------------------------------------------------------
  // Save assignments
  // ---------------------------------------------------------------------------
  const handleSave = async () => {
    if (!selectedChemId || !token) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch(`${getApiUrl()}${BASE}/${selectedChemId}`, {
        method: 'PUT',
        headers: authHeaders(token),
        body: JSON.stringify({ distributor_ids: [...assignedIds] }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setOriginalIds(new Set(assignedIds));
      showSnack(`Saved — ${assignedIds.size} distributor${assignedIds.size !== 1 ? 's' : ''} assigned.`);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Filtered lists
  // ---------------------------------------------------------------------------
  const filteredChemicals = useMemo(() => {
    const q = chemSearch.toLowerCase().trim();
    if (!q) return chemicals;
    return chemicals.filter((c) => (c.product ?? '').toLowerCase().includes(q));
  }, [chemicals, chemSearch]);

  const filteredDistributors = useMemo(() => {
    const q = distSearch.toLowerCase().trim();
    if (!q) return distributors;
    return distributors.filter((d) => (d.company ?? '').toLowerCase().includes(q));
  }, [distributors, distSearch]);

  const selectedChem = chemicals.find((c) => c.id === selectedChemId);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>Assign Chemicals to Distributors</Typography>
            <Typography variant="body2" color="text.secondary">
              Select a chemical, then tick the distributors that supply it
            </Typography>
          </Box>
          <CatalogueCountrySwitch value={country} onChange={handleCountryChange} />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>

          {/* ================================================================ */}
          {/* LEFT PANEL — Chemical list                                        */}
          {/* ================================================================ */}
          <Paper sx={{ width: 340, flexShrink: 0, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 180px)' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Chemicals
                <Chip label={`${filteredChemicals.length}`} size="small" sx={{ ml: 1 }} />
              </Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="Search chemicals…"
                value={chemSearch}
                onChange={(e) => setChemSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {chemsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={24} />
              </Box>
            ) : chemsError ? (
              <Alert severity="error" sx={{ m: 2 }}>{chemsError}</Alert>
            ) : (
              <List dense disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
                {filteredChemicals.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No chemicals found
                  </Typography>
                ) : filteredChemicals.map((chem) => (
                  <ListItemButton
                    key={chem.id}
                    selected={chem.id === selectedChemId}
                    onClick={() => handleSelectChemical(chem.id)}
                    sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                  >
                    <ListItemText
                      primary={chem.product ?? '(No name)'}
                      primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: chem.id === selectedChemId ? 600 : 400 }}
                    />
                    {chem.id === selectedChemId && isDirty && (
                      <Chip label="Unsaved" size="small" color="warning" sx={{ ml: 1 }} />
                    )}
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>

          {/* ================================================================ */}
          {/* RIGHT PANEL — Distributor checkboxes (single-column list)         */}
          {/* ================================================================ */}
          <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 180px)' }}>

            {/* Right panel header */}
            <Box sx={{ p: 2, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>Assign Distributors</Typography>
                {selectedChemId !== null && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Button
                      size="small"
                      onClick={handleSelectAll}
                      disabled={assignmentsLoading}
                      sx={{ textTransform: 'none', minWidth: 0 }}
                    >
                      Select All
                    </Button>
                    <Button
                      size="small"
                      onClick={handleDeselectAll}
                      disabled={assignmentsLoading}
                      sx={{ textTransform: 'none', minWidth: 0 }}
                    >
                      Deselect All
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon fontSize="small" />}
                      onClick={() => void handleSave()}
                      disabled={saving || assignmentsLoading || !isDirty}
                    >
                      Save
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Selected chemical name (green) */}
              {selectedChem && (
                <Typography variant="body1" fontWeight={600} color="success.main" gutterBottom>
                  Selected Chemical: {selectedChem.product ?? '(No name)'}
                </Typography>
              )}

              <TextField
                size="small"
                fullWidth
                placeholder="Search distributors…"
                value={distSearch}
                onChange={(e) => setDistSearch(e.target.value)}
                disabled={selectedChemId === null}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {saveError && (
              <Alert severity="error" sx={{ mx: 2, mt: 2 }} onClose={() => setSaveError(null)}>
                {saveError}
              </Alert>
            )}

            {/* Right panel body — single-column scrollable list */}
            <Box sx={{ overflowY: 'auto', flex: 1 }}>
              {selectedChemId === null ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
                  <Typography color="text.secondary" variant="body2">
                    ← Select a chemical to manage its distributor assignments
                  </Typography>
                </Box>
              ) : assignmentsLoading || distsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : distsError ? (
                <Alert severity="error" sx={{ m: 2 }}>{distsError}</Alert>
              ) : filteredDistributors.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No distributors found</Typography>
              ) : (
                filteredDistributors.map((dist, idx) => {
                  const address = [dist.street, dist.postcode].filter(Boolean).join(', ');
                  return (
                    <Box key={dist.id}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2,
                          py: 1.5,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                        onClick={() => handleToggle(dist.id)}
                      >
                        <Checkbox
                          size="small"
                          checked={assignedIds.has(dist.id)}
                          onChange={() => handleToggle(dist.id)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ p: 0 }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {dist.company ?? '(No name)'}
                          </Typography>
                          {address && (
                            <Typography variant="caption" color="text.secondary">
                              {address}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      {idx < filteredDistributors.length - 1 && <Divider />}
                    </Box>
                  );
                })
              )}
            </Box>

            {/* Footer note */}
            {selectedChemId !== null && (
              <Box sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">
                  Changes won&apos;t be saved until you click the Save button.
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
